import { getReferenceString, MedplumClient, resolveId } from "@medplum/core";
import { Coding, Condition, List, Observation, Reference } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";
import chunk from "lodash/chunk";

import { AppDispatch } from "@/store";
import { syncPatientPregnancyRecords, upsertPregnancyRecord } from "@/store/slices/pregnancy";
import {
  findCodingInCodeableConcept,
  getResourcesFromBundle,
  joinReferenceString,
} from "@/utils/medplum";

import { EDD_CODING } from "../constants/edd";
import { LMP_CODING } from "../constants/lmp";
import { PREGNANCY_RECORD_CODING, PREGNANCY_RECORD_CODING_QUERY } from "../constants/record";
import { PregnancyRecord } from "../types";
import { calculateEDDFromLastMenstrualPeriod } from "../utils/edd";
import { calculateGestationalAgeInWeeksAndDays } from "../utils/gestational-age";
import { selectObservationByStatusPrecedence } from "../utils/observation-precedence";
import { postpartumWeeksAndDaysAt } from "../utils/postpartum";

import { fetchPregnancyConditionForPregnancyRecord } from "./condition";
import { fetchEDDForPregnancyRecord } from "./edd";
import { fetchLastMenstrualPeriodForPregnancyRecord } from "./lmp";
import { fetchPostpartumTrackerFromPregnancyRecord } from "./postpartum";

// --- Build -------------------------------------------------------------------

/**
 * Builds a `PregnancyRecord` from a FHIR `List` resource by fetching the
 * associated pregnancy `Condition`, EDD, and LMP observations.
 *
 * Gestational age is calculated against the condition's `abatementDateTime`
 * for past pregnancies; for active ones it defaults to today.
 *
 * @param medplum - Medplum client
 * @param list - FHIR `List` resource representing the pregnancy record
 * @returns The constructed `PregnancyRecord`, or `undefined` if the list
 *   has no `id` or resolves to no patient
 */
export async function buildPregnancyRecord(
  medplum: MedplumClient,
  list: List,
): Promise<PregnancyRecord | undefined> {
  if (!list.id) return;
  const patientId = resolveId(list.subject);

  if (!patientId) return;

  const pregnancyCondition = await fetchPregnancyConditionForPregnancyRecord(medplum, list.id);
  const eddObservation = await fetchEDDForPregnancyRecord(medplum, list.id, list);
  const lastMenstrualPeriod = await fetchLastMenstrualPeriodForPregnancyRecord(medplum, list.id);

  // Prefer stored EDD (including cancelled after close-out). When search/entry lookup
  // still yields nothing, derive EDD from LMP so historical prenatal GA remains available.
  const eddValue =
    eddObservation?.valueDateTime ??
    (lastMenstrualPeriod?.valueDateTime
      ? calculateEDDFromLastMenstrualPeriod(lastMenstrualPeriod.valueDateTime)
      : undefined);

  const record: PregnancyRecord = {
    list,
    startDateTime: pregnancyCondition?.onsetDateTime ?? formatISO(new Date()),
    endDateTime: pregnancyCondition?.abatementDateTime,
    patientId,
    isActive: list.status === "current",
    edd: eddValue,
    lastMenstrualPeriod: lastMenstrualPeriod?.valueDateTime,
    gestationalAge: undefined,
    deliveryDate: undefined,
    postpartumAge: undefined,
  };

  if (record.edd) {
    record.gestationalAge = calculateGestationalAgeInWeeksAndDays(record.edd, record.endDateTime);
  }

  if (!record.isActive) {
    const postpartumTracker = await fetchPostpartumTrackerFromPregnancyRecord(medplum, list);
    const deliveryDate = postpartumTracker?.executionPeriod?.start ?? record.endDateTime;

    if (deliveryDate) {
      record.deliveryDate = deliveryDate;
      const postpartumAge = postpartumWeeksAndDaysAt(deliveryDate);
      if (postpartumAge) {
        record.postpartumAge = { weeks: postpartumAge.weeks, days: postpartumAge.days };
      }
    }
  }

  return record;
}

/**
 * Returns entries on a pregnancy `List` matching a target coding, resolved from an
 * already-fetched `resourceById` map and sorted newest-`meta.lastUpdated`-first — the same
 * ordering `_sort: "-_lastUpdated"` gives the live per-list search functions.
 *
 * @param list - Pregnancy record `List` whose entries are searched
 * @param targetCoding - `Coding` (system + code) identifying the kind of linked resource
 * @param resourceById - Resources already fetched for every `list.entry[].item` reference,
 *   keyed by `"ResourceType/id"`
 */
function findEntryResourcesByCoding<T extends Condition | Observation>(
  list: List,
  targetCoding: Coding,
  resourceById: Map<string, T>,
): T[] {
  const resources = (list.entry ?? []).reduce<T[]>((matched, entry) => {
    if (findCodingInCodeableConcept(entry.flag, targetCoding) === undefined) return matched;

    const referenceString = entry.item ? getReferenceString(entry.item as Reference) : undefined;
    const resource = referenceString ? resourceById.get(referenceString) : undefined;
    if (resource) matched.push(resource);

    return matched;
  }, []);

  return resources.sort(
    (a, b) =>
      new Date(b.meta?.lastUpdated ?? 0).getTime() - new Date(a.meta?.lastUpdated ?? 0).getTime(),
  );
}

/**
 * Picks the most recently updated pregnancy `Condition` entry on a `List`, mirroring
 * `fetchPregnancyConditionForPregnancyRecord`'s `_sort: "-_lastUpdated"` search (no status
 * preference — any status is eligible).
 */
function selectPregnancyConditionFromEntries(
  list: List,
  resourceById: Map<string, Condition>,
): Condition | undefined {
  return findEntryResourcesByCoding(list, PREGNANCY_RECORD_CODING, resourceById)[0];
}

/**
 * Picks the best-matching Observation entry on a `List` for a target coding, applying
 * {@link selectObservationByStatusPrecedence} to the entries resolved for that coding.
 */
function selectObservationFromEntries(
  list: List,
  targetCoding: Coding,
  resourceById: Map<string, Observation>,
  { allowAnyStatus = false }: { allowAnyStatus?: boolean } = {},
): Observation | undefined {
  return selectObservationByStatusPrecedence(
    findEntryResourcesByCoding(list, targetCoding, resourceById),
    { allowAnyStatus },
  );
}

/**
 * Batch-builds `PregnancyRecord`s for many `List`s in a small, fixed number of requests instead
 * of the ~4-6 per-list requests `buildPregnancyRecord` issues (Condition, EDD, LMP searches, plus
 * a postpartum Task read for retired lists). Every Condition/Observation this flow reads is
 * always registered on `list.entry` when created (see `pushToPregnancyRecord`), so every
 * candidate resource can be read in one shared `executeBatch` instead of per-list searches.
 *
 * @param medplum - Medplum client
 * @param lists - Pregnancy record `List`s to build records for
 * @returns Constructed `PregnancyRecord`s, one per list with an `id` and resolvable patient
 */
export async function buildPregnancyRecordsBatch(
  medplum: MedplumClient,
  lists: List[],
): Promise<PregnancyRecord[]> {
  const entryReferences = new Set<string>();

  for (const list of lists) {
    for (const entry of list.entry ?? []) {
      const referenceString = entry.item ? getReferenceString(entry.item as Reference) : undefined;
      if (referenceString) entryReferences.add(referenceString);
    }
  }

  const conditionByReference = new Map<string, Condition>();
  const observationByReference = new Map<string, Observation>();

  if (entryReferences.size > 0) {
    const referenceList = [...entryReferences];
    const response = await medplum.executeBatch({
      resourceType: "Bundle",
      type: "batch",
      entry: referenceList.map((reference) => ({
        request: { method: "GET" as const, url: reference },
      })),
    });

    (response.entry ?? []).forEach((entry, index) => {
      const status = entry.response?.status ?? "";
      if (!status.startsWith("2")) return;

      const resource = entry.resource;
      if (resource?.resourceType === "Condition") {
        conditionByReference.set(referenceList[index], resource);
      } else if (resource?.resourceType === "Observation") {
        observationByReference.set(referenceList[index], resource);
      }
    });
  }

  const records = await Promise.all(
    lists.map((list) =>
      buildPregnancyRecordFromCache(medplum, list, conditionByReference, observationByReference),
    ),
  );

  return records.filter((record): record is PregnancyRecord => Boolean(record));
}

/**
 * Same construction as `buildPregnancyRecord`, but resolves the linked Condition/EDD/LMP
 * Observations from already-fetched `conditionByReference`/`observationByReference` maps instead
 * of issuing live searches. Only the postpartum Task read (for retired lists) still hits the
 * network, since it's already a single direct `readResource` by id rather than part of the
 * per-list search fan-out.
 *
 * @param medplum - Medplum client, used only for the postpartum Task read on retired lists
 * @param list - FHIR `List` resource representing the pregnancy record
 * @param conditionByReference - Every `list.entry[].item` Condition already fetched, keyed by
 *   `"ResourceType/id"`, shared across all lists in the batch
 * @param observationByReference - Every `list.entry[].item` Observation already fetched, keyed by
 *   `"ResourceType/id"`, shared across all lists in the batch
 */
async function buildPregnancyRecordFromCache(
  medplum: MedplumClient,
  list: List,
  conditionByReference: Map<string, Condition>,
  observationByReference: Map<string, Observation>,
): Promise<PregnancyRecord | undefined> {
  if (!list.id) return;
  const patientId = resolveId(list.subject);

  if (!patientId) return;

  const pregnancyCondition = selectPregnancyConditionFromEntries(list, conditionByReference);
  const eddObservation = selectObservationFromEntries(list, EDD_CODING, observationByReference, {
    allowAnyStatus: true,
  });
  const lastMenstrualPeriod = selectObservationFromEntries(
    list,
    LMP_CODING,
    observationByReference,
  );

  // Prefer stored EDD (including cancelled after close-out). When search/entry lookup
  // still yields nothing, derive EDD from LMP so historical prenatal GA remains available.
  const eddValue =
    eddObservation?.valueDateTime ??
    (lastMenstrualPeriod?.valueDateTime
      ? calculateEDDFromLastMenstrualPeriod(lastMenstrualPeriod.valueDateTime)
      : undefined);

  const record: PregnancyRecord = {
    list,
    startDateTime: pregnancyCondition?.onsetDateTime ?? formatISO(new Date()),
    endDateTime: pregnancyCondition?.abatementDateTime,
    patientId,
    isActive: list.status === "current",
    edd: eddValue,
    lastMenstrualPeriod: lastMenstrualPeriod?.valueDateTime,
    gestationalAge: undefined,
    deliveryDate: undefined,
    postpartumAge: undefined,
  };

  if (record.edd) {
    record.gestationalAge = calculateGestationalAgeInWeeksAndDays(record.edd, record.endDateTime);
  }

  if (!record.isActive) {
    const postpartumTracker = await fetchPostpartumTrackerFromPregnancyRecord(medplum, list);
    const deliveryDate = postpartumTracker?.executionPeriod?.start ?? record.endDateTime;

    if (deliveryDate) {
      record.deliveryDate = deliveryDate;
      const postpartumAge = postpartumWeeksAndDaysAt(deliveryDate);
      if (postpartumAge) {
        record.postpartumAge = { weeks: postpartumAge.weeks, days: postpartumAge.days };
      }
    }
  }

  return record;
}

// --- Fetch -------------------------------------------------------------------

/**
 * Searches for pregnancy `List` resources belonging to a patient.
 *
 * @param medplum - Medplum client
 * @param patientId - The patient's FHIR resource ID or reference string (e.g. `"Patient/123"`)
 * @param options.status - FHIR `List.status` filter; defaults to `"current,retired"`
 * @returns Array of matching `List` resources sorted by last updated (newest first),
 *   or `undefined` if the search returns no bundle
 */
export async function fetchPregnancyRecordList(
  medplum: MedplumClient,
  patientReferenceString: string,
  { status = "current,retired" }: { status?: string } = {},
): Promise<List[] | undefined> {
  const { bundle } = await medplum.searchResources("List", {
    subject: patientReferenceString,
    code: PREGNANCY_RECORD_CODING_QUERY,
    status,
    _sort: "-_lastUpdated",
  });

  return getResourcesFromBundle("List", bundle) ?? [];
}

const PREGNANCY_RECORD_LIST_SUBJECT_CHUNK_SIZE = 50;

/**
 * Searches for pregnancy `List` resources belonging to multiple patients.
 *
 * Patient IDs are queried in chunks to avoid oversized FHIR search URLs.
 *
 * @param medplum - Medplum client
 * @param patientIds - Patient FHIR resource IDs
 * @param options.status - FHIR `List.status` filter; defaults to `"current,retired"`
 * @returns Matching `List` resources sorted by last updated (newest first) per search chunk
 */
export async function fetchPregnancyRecordListsByPatientIds(
  medplum: MedplumClient,
  patientIds: string[],
  { status = "current,retired" }: { status?: string } = {},
): Promise<List[]> {
  const uniquePatientIds = [...new Set(patientIds.filter(Boolean))];
  if (uniquePatientIds.length === 0) {
    return [];
  }

  const patientIdChunks = chunk(uniquePatientIds, PREGNANCY_RECORD_LIST_SUBJECT_CHUNK_SIZE);
  const results = await Promise.all(
    patientIdChunks.map(async (patientIdChunk) => {
      const subject = patientIdChunk
        .map((patientId) => joinReferenceString("Patient", patientId))
        .join(",");

      return fetchPregnancyRecordList(medplum, subject, { status });
    }),
  );

  return results.flat().filter((item) => !!item);
}

/**
 * Searches for pregnancy `List` resources belonging to a patient and builds a
 * `PregnancyRecord` for each.
 *
 * @param medplum - Medplum client
 * @param patientId - The patient's FHIR resource ID
 * @param options.status - FHIR `List.status` filter; defaults to `"current,retired"`
 * @returns Array of `PregnancyRecord` objects sorted by last updated (newest first)
 */
export async function fetchPregnancyRecords(
  medplum: MedplumClient,
  patientId: string,
  { status = "current,retired" }: { status?: string } = {},
): Promise<PregnancyRecord[]> {
  medplum.invalidateSearches("Observation");
  medplum.invalidateSearches("List");

  const patientReferenceString = joinReferenceString("Patient", patientId);
  const lists = await fetchPregnancyRecordList(medplum, patientReferenceString, { status });
  if (!lists) return [];

  const pregnancyRecords = await Promise.all(
    lists.map((list) => buildPregnancyRecord(medplum, list)),
  );

  return pregnancyRecords.filter(Boolean) as PregnancyRecord[];
}

/**
 * Searches for pregnancy `List` resources for multiple patients and builds a
 * `PregnancyRecord` for each, grouped by patient ID.
 *
 * @param medplum - Medplum client
 * @param patientIds - Patient FHIR resource IDs
 * @param options.status - FHIR `List.status` filter; defaults to `"current,retired"`
 * @returns Map of patient ID to pregnancy records for that patient (newest first per patient)
 */
export async function fetchPregnancyRecordsByPatientIds(
  medplum: MedplumClient,
  patientIds: string[],
  { status = "current,retired" }: { status?: string } = {},
): Promise<Map<string, PregnancyRecord[]>> {
  medplum.invalidateSearches("Observation");
  medplum.invalidateSearches("List");

  const lists = await fetchPregnancyRecordListsByPatientIds(medplum, patientIds, { status });
  if (lists.length === 0) {
    return new Map();
  }

  const pregnancyRecords = await buildPregnancyRecordsBatch(medplum, lists);

  const recordsByPatientId = new Map<string, PregnancyRecord[]>();

  for (const record of pregnancyRecords) {
    if (!record) {
      continue;
    }

    const existingRecords = recordsByPatientId.get(record.patientId) ?? [];
    existingRecords.push(record);
    recordsByPatientId.set(record.patientId, existingRecords);
  }

  return recordsByPatientId;
}

/**
 * Fetches all pregnancy records for a patient and reconciles the Redux store
 * against the fetched set via `syncPatientPregnancyRecords`, scoped to the
 * fetched `status` values. Records no longer returned within that scope (e.g.
 * deleted server-side) are evicted rather than left as stale entries.
 *
 * @param medplum - Medplum client
 * @param dispatch - Redux dispatch function
 * @param patientId - The patient's FHIR resource ID
 * @returns Array of fetched `PregnancyRecord` objects
 */
export async function fetchAndDispatchPregnancyRecords(
  medplum: MedplumClient,
  dispatch: AppDispatch,
  patientId: string,
  { status = "current,retired" }: { status?: string } = {},
): Promise<PregnancyRecord[]> {
  const pregnancyRecords = await fetchPregnancyRecords(medplum, patientId, { status });

  dispatch(
    syncPatientPregnancyRecords({
      patientId,
      records: pregnancyRecords,
      statuses: status.split(","),
    }),
  );

  return pregnancyRecords;
}

/**
 * Wraps any pregnancy-record fetch in a retry loop, comparing
 * `List.meta.versionId` against `previousVersionId` to detect a server-side
 * change. Breaks early once a differing versionId is observed; otherwise
 * exhausts all attempts and returns the last fetched record.
 *
 * The `fetchRecord` callback is responsible for invalidating caches before
 * each call when fresh data is required.
 *
 * @param fetchRecord - Async callback that performs one fetch of a `PregnancyRecord`
 * @param previousVersionId - `List.meta.versionId` currently held in Redux; used to detect an update
 * @param options.maxRetries - Maximum number of fetch attempts (default 3)
 * @param options.retryDelayMs - Milliseconds to wait between attempts (default 1500)
 * @returns The first record whose versionId differs from `previousVersionId`,
 *   or the last fetched record when retries are exhausted
 */
export async function fetchPregnancyRecordWithRetry(
  fetchRecord: () => Promise<PregnancyRecord | undefined>,
  previousVersionId: string | undefined,
  {
    maxRetries = 3,
    retryDelayMs = 500,
    retryWhenMissing = false,
  }: { maxRetries?: number; retryDelayMs?: number; retryWhenMissing?: boolean } = {},
): Promise<PregnancyRecord | undefined> {
  let record: PregnancyRecord | undefined;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs));
    }
    record = await fetchRecord();
    if (!record && !retryWhenMissing) {
      break;
    }
    if (
      record &&
      (previousVersionId === undefined || record.list.meta?.versionId !== previousVersionId)
    ) {
      break;
    }
  }

  return record;
}

/**
 * Fetches the patient's active (status=current) pregnancy record, invalidates
 * relevant Medplum caches before each attempt, and dispatches the result to
 * the Redux store via `upsertPregnancyRecord`.
 *
 * When `previousVersionId` is provided, the fetch is retried up to `maxRetries`
 * times until the fetched `List.meta.versionId` differs — allowing callers to
 * wait for a server-side bot to finish writing before reading stale data.
 *
 * Use this as a targeted refresh after server-side bots update only the active
 * pregnancy (e.g. LMP / EDD changes via Client History), instead of
 * re-downloading every historical record with `fetchAndDispatchPregnancyRecords`.
 *
 * @param medplum - Medplum client
 * @param dispatch - Redux dispatch function
 * @param patientId - The patient's FHIR resource ID
 * @param options.previousVersionId - `List.meta.versionId` currently in Redux; enables change detection
 * @param options.maxRetries - Maximum fetch attempts when polling for a change (default 3)
 * @returns The fetched `PregnancyRecord`, or `undefined` if no active record exists
 */
export async function fetchAndDispatchCurrentPregnancyRecord(
  medplum: MedplumClient,
  dispatch: AppDispatch,
  patientId: string,
  {
    previousVersionId,
    maxRetries,
    retryWhenMissing,
  }: { previousVersionId?: string; maxRetries?: number; retryWhenMissing?: boolean } = {},
): Promise<PregnancyRecord | undefined> {
  const patientReferenceString = joinReferenceString("Patient", patientId);

  const record = await fetchPregnancyRecordWithRetry(
    async () => {
      medplum.invalidateSearches("Observation");
      medplum.invalidateSearches("List");
      const lists = await fetchPregnancyRecordList(medplum, patientReferenceString, {
        status: "current",
      });
      const activeList = lists?.[0];
      if (!activeList) return undefined;
      return buildPregnancyRecord(medplum, activeList);
    },
    previousVersionId,
    { maxRetries, retryWhenMissing },
  );

  if (!record) return undefined;

  dispatch(upsertPregnancyRecord({ patientId, record }));
  return record;
}
